import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useAppContext } from '@prisjakt/ui-app-context';

import { H3Text } from '@prisjakt/ui/text/variants';
import {
  VisualNavigation,
  VisualNavigationLink,
  VisualNavigationLinkProps,
} from '@prisjakt/ui/visual-navigation';

import { useEffectOnMount } from 'src/hooks/useEffectOnMount';

import {
  trackSearchResultStoreClicked,
  trackSearchResultStoresListed,
} from '../../tracking';

import { Store, Maybe } from 'src/types';
import { TabAnchor } from '../navigation/types';

import { Section } from './styles';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

type Props = {
  active: boolean;
  count: number;
  query: string;
  shops: Maybe<Omit<Store, 'userReviewSummary' | 'countryCode' | 'currency'>>[];
  visible: boolean;
  wideContent: boolean;
};

type FormattedLink = {
  linkProps: VisualNavigationLinkProps;
  path: string;
};

export const ShopResults = (props: Props) => {
  const { active, count, query, shops, wideContent, visible } = props;
  const { market, t } = useAppContext();

  useEffectOnMount(() => {
    if (shops.length) trackSearchResultStoresListed();
  });

  const formattedLinks: FormattedLink[] = useMemo(
    () =>
      shops?.map(shop => {
        const imageSrc = shop?.hasLogo
          ? `${market.imageCdnUrl}g/ftg_logos/store_${shop.id}.png`
          : undefined;

        const link: VisualNavigationLinkProps = {
          label: shop?.name || '',
          imageSrc,
          linkContainer: Link,
          linkContainerProps: {
            to: shop?.pathName,
            'data-test': 'InternalLink',
            onClick: () =>
              trackSearchResultStoreClicked(
                shop?.id?.toString() || '',
                shop?.name || '',
                query
              ),
          },
        };
        return { linkProps: link, path: shop?.pathName || '' };
      }) ?? [],

    [market.imageCdnUrl, query, shops]
  );

  if (!shops.length) return null;

  return (
    <Section id={TabAnchor.STORES} $visible={visible}>
      <H3Text>{`${t('search', 'stores.title')} (${count})`}</H3Text>

      <VisualNavigation compact={!active} linksPerRow={wideContent ? 5 : 4}>
        {formattedLinks.map((link, index) => (
          <VisualNavigationLink
            {...link.linkProps}
            key={link.path}
            containerProps={{
              onClick: () =>
                trackInternalNavigationClick({
                  scope: InternalNavigationScope.STORE_CARD,
                  nextPage: link.path,
                  index,
                }),
            }}
          />
        ))}
      </VisualNavigation>
    </Section>
  );
};
